import type { DecisionGraph } from "@imminently/decisively-core";
import { setWith } from "lodash";
import type { TreeProps } from "../../DataTree/constants";

type Items = TreeProps[ "items" ];

function sortTreeItemsAsc( item: Items[ 0 ] ): typeof item {
  const { children } = item;

  if (children.length === 0) return item;

  const mappedChildren = children.map(child => sortTreeItemsAsc(child));

  return {
    ...item,
    children: mappedChildren.slice().sort((a, b) => a.info.text.localeCompare(b.info.text))
  };
}

export function gatherAllIds(n: Items[ 0 ]): string[] {
  return [n.id].concat(
    n.children.reduce< string[] >((a, child) => a.concat(gatherAllIds(child)), []),
  );
}

// we just need "some" type to describe nodes. It does not
// need to be precise, just (kinda) distinct
type SimpleNode = { id: string, description?: string; value?: unknown; };
type PathBasedTree = {
  [ uuidOrEntityName: string ]: SimpleNode | { [ instanceId: string ]: PathBasedTree }
}

/**
 * The batch debug data tree: the graph's input attributes (listed nodes that aren't hidden, identifiers or derived from
 * others) under `global`, nested by entity and instance along their paths, sorted by text. Empty without a graph.
 */
export function dataTreeItems( graph: DecisionGraph | undefined ): Items {
  if (!graph) return [];

  const pathBasedTree = graph.ids().reduce< PathBasedTree >(
    (a, id) => {
      const shouldOmitNode = Boolean(
        false
        || graph.field( id, "hidden" )
        || graph.field( id, "identifier" )
        || graph.successors( id ).length
      );
      if ( shouldOmitNode ) return a;

      const node: SimpleNode = {
        id: graph.guid( id ),
        description: graph.field( id, "description" ) as string | undefined,
        // its input, else its derived value (an instance's falling back to its template's)
        value: graph.result( id ),
      };

      return setWith( a, graph.deref( id ).split( "/" ), node, Object );
    },
    {} as PathBasedTree
  );

  const pathBasedTreeNodeToItem = (n: PathBasedTree, key: string): Items[ 0 ] => {
    const keys = Object.keys(n);

    return {
      id: key,
      info: { text: key.replace("global/", ""), value: key === "global" ? "entity" : "instance" },
      children: keys.map< Items[ 0 ] >(instanceProp => {
        const node = n[ instanceProp ];

        if ("id" in node) {
          const typedN = node as SimpleNode;

          return {
            id: [ key, node.id ].join( "/" ),
            children: [],
            info: {
              text: typedN.description || "",
              value: (typedN.value ?? undefined) as any,
            }
          };
        }

        const instanceIds = Object.keys( node );
        const id = [key, instanceProp].join( "/" );

        return {
          id: id,
          info: { text: instanceProp, value: "entity" },
          children: instanceIds.map(
            instanceId => pathBasedTreeNodeToItem( node[ instanceId ], [ id, instanceId ].join( "/" ) )
          )
        };
      })
    };
  };

  return sortTreeItemsAsc( {
    id: "",
    children: [ pathBasedTreeNodeToItem( pathBasedTree, "global" ) ],
    info: { text: "" }
  } ).children;
}
